import requireAuth from '@components/hoc/with-required-auth-hoc';
import SubpageTitle from '@components/menu-items/subpage-title';
import { Box, Grid, Typography } from '@mui/material';
import Column from '@components/layout-util-components/column';
import { InputSkeleton } from '@components/skeletons';
import WCTTextField from '@components/input/text-field';
import useCompanySystemText from './company-system-text-hook';
import { useNavigate } from 'react-router-dom';
import { OutlinedButton, PrimaryButton } from '@components/buttons';
import Spacer from '@components/layout-util-components/spacer';
import useSystemText from '@hooks/use-system-text';
import { Gap } from '@components/layout-util-components/gap';

function CompanySystemTextPage() {
  const { systemText } = useSystemText();
  const navigate = useNavigate();
  const {
    isLoading,
    focalPoint,
    businessFunctions,
    recurrence,
    typeOfImpact,
    impactCategory,
    inviteOnly,
    shareWithGroups,
    isSubmitting,
    canSubmit,
    submit,
  } = useCompanySystemText();

  return (
    <>
      <SubpageTitle breadCrumb="RCA Settings" title="Label Configuration" />
      <Gap size={32} />
      <Grid container>
        <Grid item xs={12} md={6}>
          <Typography variant="body1">Step 1 - Overview</Typography>
          <Typography variant="body2">
            You can override the labels displayed within the overview stage of
            the 'Create RCA' process
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Column>
            {isLoading ? (
              <InputSkeleton />
            ) : (
              <WCTTextField
                label={systemText['RCA.Overview.FocalPoint']}
                name="focalPoint"
                value={focalPoint.value}
                error={focalPoint.error}
                onChange={focalPoint.set}
                required
              />
            )}
            <Gap size={24} />
            {isLoading ? (
              <InputSkeleton />
            ) : (
              <WCTTextField
                label="Business Functions"
                name="businessFunctions"
                value={businessFunctions.value}
                error={businessFunctions.error}
                onChange={businessFunctions.set}
                required
              />
            )}
            <Gap size={24} />
            {isLoading ? (
              <InputSkeleton />
            ) : (
              <WCTTextField
                label="Recurrence"
                name="recurrence"
                value={recurrence.value}
                error={recurrence.error}
                onChange={recurrence.set}
                required
              />
            )}
          </Column>
        </Grid>
      </Grid>
      <Gap size={20} />
      <hr />
      <Gap size={20} />
      <Grid container>
        <Grid item xs={12} md={6}>
          <Typography variant="body1">Step 2 - Details</Typography>
          <Typography variant="body2">
            You can override the labels displayed within the details stage of
            the 'Create RCA' process
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Column>
            {isLoading ? (
              <InputSkeleton />
            ) : (
              <WCTTextField
                label="Type of impact"
                name="typeOfImpact"
                value={typeOfImpact.value}
                error={typeOfImpact.error}
                onChange={typeOfImpact.set}
                required
              />
            )}
            <Gap size={24} />
            {isLoading ? (
              <InputSkeleton />
            ) : (
              <WCTTextField
                label="Impact Category"
                name="impactCategory"
                value={impactCategory.value}
                error={impactCategory.error}
                onChange={impactCategory.set}
                required
              />
            )}
          </Column>
        </Grid>
      </Grid>
      <Gap size={20} />
      <hr />
      <Gap size={20} />
      <Grid container>
        <Grid item xs={12} md={6}>
          <Typography variant="body1">Step 3 - Collaboration</Typography>
          <Typography variant="body2">
            You can override the labels displayed within the collaboration stage
            of the 'Create RCA' process
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Column>
            {isLoading ? (
              <InputSkeleton />
            ) : (
              <WCTTextField
                label="Invite only / Public"
                name="inviteOnly"
                value={inviteOnly.value}
                error={inviteOnly.error}
                onChange={inviteOnly.set}
                required
              />
            )}
            <Gap size={24} />
            {isLoading ? (
              <InputSkeleton />
            ) : (
              <WCTTextField
                label="Share with groups"
                name="shareWithGroups"
                value={shareWithGroups.value}
                error={shareWithGroups.error}
                onChange={shareWithGroups.set}
                required
              />
            )}
          </Column>
        </Grid>
      </Grid>
      <Gap size={32} />
      <Box sx={{ display: 'flex' }}>
        <OutlinedButton
          onClick={() => navigate('/company-profile/rca-settings')}
        >
          Cancel
        </OutlinedButton>
        <Spacer />
        <PrimaryButton
          disabled={!canSubmit}
          isBusy={isSubmitting}
          onClick={submit}
        >
          Update Settings
        </PrimaryButton>
      </Box>
    </>
  );
}

export default requireAuth(CompanySystemTextPage);
